export type AppState = {
    enteredInformation: boolean,
    firstName: string,
    lastName: string,
    showChat: boolean,
    lastMessage: string,
    hasOpened: boolean,
    hasShownPrompt: boolean,
    lastLocation: string,
    shrinkWidget: boolean,
    isLandscape: boolean,
    proactiveTeaserText: string,
    proactiveMessageId: string,
    proactiveUnread: boolean,
    proactiveUnreadCount: number
}

export const initialState: AppState = {
    enteredInformation: false,
    firstName: "",
    lastName: "",
    showChat: false,
    hasOpened: false,
    hasShownPrompt: false,
    lastMessage: "",
    lastLocation: "",
    shrinkWidget: false,
    isLandscape: true,
    proactiveTeaserText: "",
    proactiveMessageId: "",
    proactiveUnread: false,
    proactiveUnreadCount: 0
}

export type AppAction =
    | { type: 'setEnteredInformation', hasEnteredInformation: boolean }
    | { type: 'setShowChat', showChat: boolean }
    | { type: 'setShrinkWidget', shrinkWidget: boolean }
    | { type: 'setHasOpened', hasOpened: boolean }
    | { type: 'setHasShownPrompt', hasShownPrompt: boolean }
    | { type: 'setLastLocation', location: string }
    | { type: 'setIsLandscape', isLandscape: boolean }
    | { type: 'setProactiveTeaser', text: string, proactiveMessageId: string }
    | { type: 'clearProactiveTeaser' }
    | { type: 'setAppState', appState: AppState }

export const storageIdentifier = "edullaStartChat"

const getStorageIdentifier = () => {
    const tid = sessionStorage.getItem("edullaTid") ?? "";
    return `${storageIdentifier}${tid}`;
}

const normalizeAppState = (state?: Partial<AppState> | null): AppState => {
    const merged = { ...initialState, ...(state ?? {}) } as Partial<AppState>;

    const coerceString = (value: unknown, fallback = "") => typeof value === "string" ? value : fallback;
    const coerceBoolean = (value: unknown, fallback = false) => typeof value === "boolean" ? value : fallback;
    const coerceCount = (value: unknown) => typeof value === "number" && Number.isFinite(value) && value > 0 ? Math.floor(value) : 0;

    let lastLocation = coerceString(merged.lastLocation, initialState.lastLocation).trim();
    if (!lastLocation || lastLocation === "undefined" || lastLocation === "/index.html") {
        lastLocation = initialState.lastLocation;
    } else if (!lastLocation.startsWith("/")) {
        lastLocation = `/${lastLocation}`;
    }

    return {
        enteredInformation: coerceBoolean(merged.enteredInformation),
        firstName: coerceString(merged.firstName),
        lastName: coerceString(merged.lastName),
        showChat: coerceBoolean(merged.showChat),
        lastMessage: coerceString(merged.lastMessage),
        hasOpened: coerceBoolean(merged.hasOpened),
        hasShownPrompt: coerceBoolean(merged.hasShownPrompt),
        lastLocation,
        shrinkWidget: coerceBoolean(merged.shrinkWidget),
        isLandscape: coerceBoolean(merged.isLandscape, initialState.isLandscape),
        proactiveTeaserText: coerceString(merged.proactiveTeaserText),
        proactiveMessageId: coerceString(merged.proactiveMessageId),
        proactiveUnread: coerceBoolean(merged.proactiveUnread),
        proactiveUnreadCount: coerceCount(merged.proactiveUnreadCount)
    };
}

const persistState = (state: AppState): AppState => {
    const normalized = normalizeAppState(state);
    localStorage.setItem(getStorageIdentifier(), JSON.stringify(normalized));
    return normalized;
}

const getStoredState = (): AppState => {
    const storedValue = localStorage.getItem(getStorageIdentifier());
    if (!storedValue) {
        return initialState;
    }

    try {
        const parsed = JSON.parse(storedValue);
        return normalizeAppState(parsed);
    } catch (err) {
        return initialState;
    }
}

export const getCachedAppState = (): AppState => getStoredState();

export const startReducer = (state: AppState, action: AppAction) => {
    const currentState = getStoredState();

    switch (action.type) {
        case "setEnteredInformation":
            return persistState({ ...currentState, enteredInformation: action.hasEnteredInformation });
        case "setShowChat":
            // Opening the chat clears any pending teaser/unread state, since the
            // proactive message is now visible inside the (revealed) iframe.
            return persistState({
                ...currentState,
                showChat: action.showChat,
                ...(action.showChat ? { proactiveTeaserText: "", proactiveMessageId: "", proactiveUnread: false, proactiveUnreadCount: 0 } : {})
            });
        case "setHasOpened":
            return persistState({ ...currentState, hasOpened: action.hasOpened });
        case "setLastLocation":
            return persistState({ ...currentState, lastLocation: action.location });
        case "setShrinkWidget":
            return persistState({ ...currentState, shrinkWidget: action.shrinkWidget });
        case "setIsLandscape":
            return persistState({ ...currentState, isLandscape: action.isLandscape });
        case "setHasShownPrompt":
            return persistState({ ...currentState, hasShownPrompt: action.hasShownPrompt });
        case "setProactiveTeaser": {
            // A message that arrives while the chat is open is already on
            // screen — there is nothing unread to track (spec:
            // proactive-rendering, "No teaser while the chat is open").
            // Storing it would resurrect a stale teaser + badge on the next
            // minimize, even after the visitor replied.
            if (currentState.showChat) {
                return currentState;
            }
            // Count distinct unread proactive messages. A repeat of the same id
            // (e.g. an iframe replay/remount) updates the text without re-counting.
            const isNewMessage = !!action.proactiveMessageId && action.proactiveMessageId !== currentState.proactiveMessageId;
            const nextCount = isNewMessage
                ? (currentState.proactiveUnreadCount || 0) + 1
                : (currentState.proactiveUnreadCount || 1);
            return persistState({
                ...currentState,
                proactiveTeaserText: action.text,
                proactiveMessageId: action.proactiveMessageId,
                proactiveUnread: true,
                proactiveUnreadCount: nextCount
            });
        }
        case "clearProactiveTeaser":
            return persistState({
                ...currentState,
                proactiveTeaserText: "",
                proactiveMessageId: "",
                proactiveUnread: false,
                proactiveUnreadCount: 0
            });
        case "setAppState":
            return persistState(action.appState);
        default:
            return currentState;
    }
};

